import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SideBar from './SideBar';
import TopHeader from './TopHeader';
import './Layout.scss';
import { useAuth } from '../../contexts/AuthContext';
import MarketplaceOrgNameModal from '../marketplace/MarketplaceOrgNameModal';
import DemoProxyBanner from './DemoProxyBanner';
import { registerMobileSidebarControls } from '../tutorial/tourMobileSidebar';

export const MAIN_CONTENT_ID = 'main-content';

// Every route renders its own <Layout>, so this module-level flag (not a ref) is
// what tells the first page load apart from later client-side navigations.
let hasRenderedFirstPage = false;

interface LayoutProps {
    children: React.ReactNode;
    hideSidebar?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, hideSidebar = false }) => {
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    // Separate from isSidebarCollapsed, which is the desktop 260px<->60px width
    // toggle — this is the mobile off-canvas drawer's open/closed state.
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const { currentUser, userData, logout, orgNamePending, refreshAuth } = useAuth();
    const { t } = useTranslation();
    const location = useLocation();
    const mainRef = useRef<HTMLElement>(null);

    const toggleSidebar = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
    };

    const toggleMobileSidebar = () => setIsMobileSidebarOpen((open) => !open);
    const closeMobileSidebar = () => setIsMobileSidebarOpen(false);

    // Close the mobile drawer on navigation so it doesn't stay open over the next page.
    useEffect(() => {
        setIsMobileSidebarOpen(false);
    }, [location.pathname]);

    // A client-side navigation leaves focus on <body> (the clicked link unmounts with
    // the old page), so screen-reader and keyboard users would restart from the top of
    // the document. Move focus to <main> instead, as a full page load effectively does
    // (WCAG 2.4.3). Skipped on first load — Tab should reach the skip link first — and
    // whenever the new page, an open dialog or a tutorial popover already placed focus.
    useEffect(() => {
        if (!hasRenderedFirstPage) {
            hasRenderedFirstPage = true;
            return;
        }
        const frame = requestAnimationFrame(() => {
            const active = document.activeElement;
            const focusIsPlaced = active && active !== document.body && (
                mainRef.current?.contains(active) ||
                active.closest('[role="dialog"], [role="alertdialog"], .driver-popover')
            );
            if (!focusIsPlaced) mainRef.current?.focus({ preventScroll: true });
        });
        return () => cancelAnimationFrame(frame);
    }, [location.pathname]);

    const skipToMain = (event: React.MouseEvent<HTMLAnchorElement>) => {
        // Focus directly instead of following the hash, which would add a history entry.
        event.preventDefault();
        mainRef.current?.focus();
    };

    // Let the tutorial system open/close the mobile drawer around sidebar-nav
    // steps (see components/tutorial/tourMobileSidebar.ts) without needing a
    // direct import cycle between the tutorial and layout modules.
    useEffect(() => {
        registerMobileSidebarControls({
            open: () => setIsMobileSidebarOpen(true),
            close: () => setIsMobileSidebarOpen(false),
        });
        return () => registerMobileSidebarControls(null);
    }, []);

    // Create a complete user object with all necessary data
    const userWithRole = currentUser ? {
        ...currentUser,
        role: userData?.role || 'trainee',
    } : null;

    // Current org name — pre-fill the modal input so the purchaser can edit it.
    const currentOrgName = (userData as any)?.organization?.name || '';

    return (
        <div className={`layout ${isSidebarCollapsed ? 'sidebar-collapsed' : ''} ${hideSidebar ? 'no-sidebar' : ''}`}>
            {/* First tab stop on every page: skips the sidebar and header (WCAG 2.4.1). */}
            <a href={`#${MAIN_CONTENT_ID}`} className="skip-link" onClick={skipToMain}>
                {t('common.skipToMainContent')}
            </a>
            {!hideSidebar && (
                <SideBar
                    isCollapsed={isSidebarCollapsed}
                    onToggle={toggleSidebar}
                    isMobileOpen={isMobileSidebarOpen}
                    onCloseMobile={closeMobileSidebar}
                />
            )}
            <div className="layout-main">
                <TopHeader
                    currentUser={userWithRole}
                    onLogout={logout}
                    isMobileSidebarOpen={isMobileSidebarOpen}
                    onToggleMobileSidebar={toggleMobileSidebar}
                    hideSidebarToggle={hideSidebar}
                />
                <DemoProxyBanner />
                <main id={MAIN_CONTENT_ID} ref={mainRef} tabIndex={-1} className="layout-content">
                    {children}
                </main>
            </div>

            {/* Org name confirmation modal — shown once to the Marketplace purchaser */}
            {orgNamePending && (
                <MarketplaceOrgNameModal
                    currentOrgName={currentOrgName}
                    onConfirmed={refreshAuth}
                />
            )}
        </div>
    );
};

export default Layout;